import { ReactElement, useState, useCallback } from 'react';
import { useRoutes, useAwsEffect, useAwsGetter } from '@localstack/services';
import { Card, Button } from '@mui/material';
import { DEFAULT_API_GATEWAY_ROUTES } from '@localstack/constants';
import { useParams } from 'react-router-dom';

import { PageTitle, Dropdown, ButtonWithDialog, AgwApiStagesTable } from '@localstack/ui';

import { ApiGatewayProps } from './types';
import { ApiNavTabs } from './components';

export const AgwApiStages = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_API_GATEWAY_ROUTES,
}: ApiGatewayProps): ReactElement => {
  const { apiId: ApiId } = useParams<'apiId'>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { goto } = useRoutes();
  const { data: api } = useAwsGetter('ApiGatewayV2', 'getApi', [{ ApiId }], { clientOverrides });

  const {
    data: stages,
    isLoading,
    mutate,
  } = useAwsGetter('ApiGatewayV2', 'getStages', [{ ApiId }], { clientOverrides });

  const { deleteStage } = useAwsEffect('ApiGatewayV2', ['deleteStage'], {
    revalidate: ['getStage', 'getStages'],
    clientOverrides,
  });

  const handleDeleteSelected = useCallback(async () => {
    const promises = selectedIds.map((StageName) => deleteStage({ ApiId: ApiId as string, StageName }));
    await Promise.all(promises);
  }, [selectedIds]);

  return (
    <Layout
      documentTitle="API Stages"
      title={
        <PageTitle
          title="API Stages"
          onMutate={mutate}
          breadcrumbs={[
            ['API Gateway', () => goto(routes.RESOURCES_AGW2)],
            [api?.Name, () => goto(routes.RESOURCES_AGW2_API, { apiId: ApiId })],
            ['Stages', null],
          ]}
        />
      }
      tabs={<ApiNavTabs apiId={ApiId as string} routes={routes} />}
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_AGW2_API_STAGE_CREATE, { apiId: ApiId })}>
            Create Stage
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={selectedIds.length === 0 || isLoading}
              dialogTitle={`Delete ${selectedIds.length} Stage(s)?`}
              onClick={handleDeleteSelected}
              dialogContent="Selected Stages will be permanently deleted"
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <AgwApiStagesTable
          apiId={ApiId as string}
          stages={stages?.Items || []}
          onSelect={setSelectedIds}
          onViewStage={({ StageName }) => goto(routes.RESOURCES_AGW2_API_STAGE, { apiId: ApiId, stageName: StageName })}
        />
      </Card>
    </Layout>
  );
};
